import loadGMaps from 'load-google-maps-api';
import Lang from 'lang';
import addIntToHex from '../functions/addIntToHex';
import GlobalAlerts from 'global_alerts';
import ga4 from '../ga4/ga4.js';

class Gmaps {
  constructor(el, googleMaps) {
    this._initializeProperties();

    this.mapElement = el;
    this.googleMaps = googleMaps;
    this.map = new this.googleMaps.Map(el, {
      // Centro são paulo
      center: {
        lat: -23.533773,
        lng: -46.625290
      },
      zoom: 13,
      maxZoom: /^(dev.)?admin/.test(window.location.hostname) ? null : 15, // Nao ter zoom maximo no admin
      mapTypeId: 'roadmap'
    });

    this.mapElement.addEventListener('mapShown', () => {
      if (this.customPolylines.length) {
        this.map.fitBounds(this.gmapsPointsBounds);
      }
    });

    // Se tiver template para adicionar campos de pesquisa do mapa
    this.waypointTemplate = document.querySelector('[data-gmaps-waypoint-template="' + this.mapElement.id + '"]');
    if (this.waypointTemplate) {
      // Inicia os servicos de rotas do google maps
      this.directionsService = new googleMaps.DirectionsService;
      this.directionsDisplay = new googleMaps.DirectionsRenderer({
        polylineOptions: {
          geodesic: true,
          strokeColor: this.polylineStrokeColor,
          strokeWeight: this.polylineStrokeWeight,
        },
        suppressMarkers: true
      });

      // Se tiver um botão no html para adicionar mais campos de pesquisa
      this.btnAddWaypoint = document.querySelector('[data-gmaps-add-waypoint="' + this.mapElement.id + '"]');
      if (this.btnAddWaypoint) {
        // Evento ao clicar no botão para adicionar campo de pesquisa
        this.btnAddWaypoint.addEventListener('click', () => {
          this.createNewInputFromTemplate();
        });
      }
    }

    // Se tiver os pontos pré-definidos, traça a rota,
    // senão irá colocar no mapa a posicao atual do navegador
    this.gmapPoints = this._getGmapPoints();
    if (this.gmapPoints) {
      const searchedPoints = document.querySelector('[data-gmaps-searched-points="' + this.mapElement.id + '"]');
      if (searchedPoints) {
        const pontos = JSON.parse(searchedPoints.value);
        pontos.forEach((data, index) => {
          if (this.waypointTemplate) {
            this.createNewInputFromTemplate(data['placeId'], data['search']);
          }

          const [lat, lng] = data['location'].split(',');
          const latLng = {lat: parseFloat(lat), lng: parseFloat(lng)};
          if (index === 0) {
            this.makeInitialMarker(this.gmapPoints[0], data['search']);
          } else if (index === pontos.length - 1) {
            this.makeFinalMarker(this.gmapPoints[this.gmapPoints.length - 1], data['search']);
          } else {
            this.makeWaypointMarker(latLng, data['search']);
          }
        });
      }

      this.traceCustomRoute(!!searchedPoints);
    } else {
      // Cria 2 inputs de pesquisa inicial e final caso não tenha rota ja salva
      this.createNewInputFromTemplate();
      this.createNewInputFromTemplate();

      // Handler para evento de quando a localização atual do usuario é
      // atualizada
      document.addEventListener('locationUpdated', (event) => {
        // Se ja tiver rota marcada no mapa, não atualiza a posição do mapa
        if (this.markers.length) {
          return;
        }

        const position = event.detail;

        // Pega posição atual do navegador e seta no mapa
        this.map.setCenter({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      });
    }

    // Altura padrão da div do mapa
    this.mapElement.style.height = '500px';
  }

  /**
   * Lista de todas as propriedades e seus valores padrão
   *
   * @return void
   */
  _initializeProperties() {
    this.mapElement = null;
    this.googleMaps = null;
    this.map = null;
    this.gmapPoints = null;
    this.gmapsPointsBounds = null;
    this.waypointTemplate = null;
    this.directionsService = null;
    this.directionsDisplay = null;
    this.btnAddWaypoint = null;

    this.markers = [];
    this.searchedMapPoints = [];
    this.polylineStrokeColor = '#00c7e4';
    this.polylineStrokeWeight = 10;
    this.customPolylines = [];
    this.countSearchInputs = 0;
    this.maxSearchInputs = 8;
  }

  /**
   * Função que determina os pontos do google maps
   *
   * @return array
   */
  _getGmapPoints() {
    // Busca por pontos pré definidos no elemento do mapa no DOM
    const encodedPolyline = this.mapElement.dataset.gmapPolyline;
    if (encodedPolyline) {
      return this.decodePath(encodedPolyline);
    } else if (this.mapElement.dataset.gmapPontos) {
      return JSON.parse(this.mapElement.dataset.gmapPontos);
    }
  }

  /**
   * Decodifica a string da polyline no mapa e retorna
   * no formato esperado {lat: 123, lng: 321}
   *
   * @param  string encodedPolyline
   *
   * @return array
   */
  decodePath(encodedPolyline) {
    const decodedPath = this.googleMaps.geometry.encoding.decodePath(encodedPolyline);
    return decodedPath.map(function (point) {
      return {
        'lat': point.lat(),
        'lng': point.lng()
      };
    });
  }

  /**
   * Cria um novo campo de pesquisa do mapa, utilizando o template salvo
   *
   * @param  string place_id
   * @param  string searchFieldValue
   *
   * @return void
   */
  createNewInputFromTemplate(place_id = null, searchFieldValue = null) {
    // Se tiver excedido o maximo de campos de pesquisa permitido,
    // cancela a criação do campo
    if (this.countSearchInputs >= this.maxSearchInputs) {
      return;
    }

    // Copia o template e seus filhos para um novo input
    const newWaypoint = this.waypointTemplate.cloneNode(true);
    newWaypoint.classList.remove('hidden');

    // Pega o input de autocomplete do google dentro da nova copia
    // do template criado, e faz o setup desse input
    const autocompleteInput = newWaypoint.querySelector('[data-gmaps-search]');
    const fingerprint = this.setupAutocompleteInput(autocompleteInput);

    autocompleteInput.value = searchFieldValue;
    autocompleteInput.name = autocompleteInput.name
      .replace('[template]', '[' + (place_id ? place_id : '') + ']');

    if (place_id) {
      this.searchedMapPoints[fingerprint] = {'placeId': place_id};
    }

    // Pega o botão de remover o campo de pesquisa
    const btnRemovePoint = newWaypoint.querySelector('[data-gmaps-point-remove]');
    btnRemovePoint.addEventListener('click', () => {
      // Remove o campo de pesquisa dos locais ja pesquisados e salvos nessa classe
      delete this.searchedMapPoints[fingerprint];

      // Remove a div pai que engloba o template inteiro referente ao botão
      // de deletar clicado
      btnRemovePoint.closest('[data-gmaps-waypoint-template]').remove();

      // Decrementa 1 do contador de campos de pesquisa
      this.incrementCountSearchInputs(-1);
      ga4.event('-expensesModalMap_removePath')();
      // Atualiza a rota
      this.searchRoute();
    });

    // Adiciona o novo campo antes do template no HTML
    this.waypointTemplate.parentElement.insertBefore(newWaypoint, this.waypointTemplate);

    // Incrementa 1 do contador de campos de pesquisa
    this.incrementCountSearchInputs(1);
  }

  /**
   * Faz o gerenciamento de eventos do componente de autocomplete do input
   * de pesquisa de lugares no mapa
   *
   * @param  DOMElement autocompleteInput
   *
   * @return int
   */
  setupAutocompleteInput(autocompleteInput) {
    const autocomplete = new this.googleMaps.places.Autocomplete(
      autocompleteInput,
      {
        fields: ['place_id']
      }
    );

    // Faz uma digital do campo de pesquisa e salva em um atributo para
    // recuperação do dado posteriormente
    const fingerprint = Number(new Date) + this.countSearchInputs;

    // Coloca o mapa atual como referencia proxima para as pesquisas
    // do input (google)
    autocomplete.bindTo('bounds', this.map);
    // Evento que pega a selecao de um lugar no input autocomplete
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();

      // Salva o local pesquisado
      this.searchedMapPoints[fingerprint] = {'placeId': place.place_id};

      autocompleteInput.name = autocompleteInput.name
        .replace(/\[\w*\]/, '[' + place.place_id + ']');

      // Atualiza o mapa
      this.searchRoute();
    });

    return fingerprint;
  }

  /**
   * Incrementa o contador de campos de pesquisa, e faz algumas
   * funcoes customizadas de acordo com a quantidade de campos
   * de pesquisa existentes
   *
   * @param  int value
   *
   * @return void
   */
  incrementCountSearchInputs(value) {
    this.countSearchInputs += value;

    // Função para mostrar/esconder os botões de remover os campos
    // de pesquisa, o minimo para aparecer é 2 inputs
    const removeButtons = document.querySelectorAll('[data-gmaps-point-remove="' + this.mapElement.id + '"]');
    removeButtons.forEach((el) => {
      if (this.countSearchInputs <= 2) {
        el.classList.add('hidden');
      } else {
        el.classList.remove('hidden');
      }
    });

    // Se existir um botão de adicionar mais pontos
    if (this.btnAddWaypoint) {
      // Esconde o botão caso tenha chegado no limite de campos
      // de pesquisa, ou mostra-o no caso de o usuario ter
      // deletado um campo de pesquisa e o contador estiver abaixo do limite
      if (this.countSearchInputs < this.maxSearchInputs) {
        this.btnAddWaypoint.classList.remove('hidden');
      } else {
        this.btnAddWaypoint.classList.add('hidden');
      }
    }
  }

  /**
   * Metodo que faz a busca da rota sugerida pelo google
   * de acordo com os pontos inseridos
   *
   * @return void
   */
  searchRoute() {
    // Necessário estar definido pelo menos 2 pontos
    if (Object.keys(this.searchedMapPoints).length < 2) {
      // Remove a rota do mapa
      this.clearMarkers();
      this.clearPolylines();
      this.directionsDisplay.setMap(null);

      // Envia evento com rota nula (sem rota definida)
      const event = new CustomEvent('routeProcessed');
      this.mapElement.dispatchEvent(event);

      return;
    }

    //Pontos ordenados na ordem dos inputs no HTML
    let waypoints = [];
    Object.keys(this.searchedMapPoints).sort().forEach((key) => {
      waypoints.push(this.searchedMapPoints[key]);
    });

    const origin = waypoints.shift();
    const destination = waypoints.pop();

    waypoints = waypoints.map(function (placeId) {
      return {'location': placeId};
    });

    // Faz a busca da rota entre os locais inseridos
    this.directionsService.route({
      origin: origin,
      destination: destination,
      waypoints: waypoints,
      optimizeWaypoints: false,
      travelMode: 'DRIVING'
    }, (response, status) => {
      if (status === 'OK') {
        this.clearMarkers();
        this.clearPolylines();

        // Desenha a polyline (trajetoria) no mapa do resultado obtido
        this.directionsDisplay.setMap(this.map);
        this.directionsDisplay.setDirections(response);

        // Desenha o marcador customizado inicial, final e paradas no mapa
        const legs = response.routes[0].legs;
        if (legs.length > 1) {
          for (let i = 0; i < legs.length - 1; i++) {
            this.makeWaypointMarker(legs[i].end_location, legs[i].end_address);
          }
        }
        this.makeInitialMarker(legs[0].start_location, legs[0].start_address);
        this.makeFinalMarker(legs[legs.length - 1].end_location, legs[legs.length - 1].end_address);

        // Dispara um evento sobre o mapa de que a rota foi processada
        // para outras funcoes conseguirem lidar com o resultado
        const event = new CustomEvent('routeProcessed', {
          'detail': response
        });
        this.mapElement.dispatchEvent(event);
      } else {
        GlobalAlerts.createAndShow({
          title: Lang.get('js/src/classes/gmaps.ops'),
          message: Lang.get('js/src/classes/gmaps.gmaps_failed_route_search', {'status': status}),
          type: 'danger'
        });
      }
    });
  }

  /**
   * Traça a rota pre-definida no mapa
   *
   * @param  boolean
   *
   * @return void
   */
  traceCustomRoute(supressMarkers = false) {
    // Cria um bounds de Lat,Lng para centralizar o mapa e o zoom
    // de acordo com o percurso
    this.gmapsPointsBounds = new this.googleMaps.LatLngBounds();
    this.gmapPoints.forEach((point) => {
      this.gmapsPointsBounds.extend(point);
    });
    this.map.fitBounds(this.gmapsPointsBounds);

    const domainName = window.location.hostname.split('.');
    // Se estiver no dev, deverá ignorar o primeiro subdominio para descobrir
    // se está na pagina de admin
    const subDomain = domainName[0] === 'dev' ? domainName[1] : domainName[0]
    const subDomainInput = document.getElementById('subdomainAdmin')
      ? document.getElementById('subdomainAdmin').value
      : 'gesti';

    if (subDomain === subDomainInput && this.gmapPoints.length >= 255) {
      this.customPolylines = this.traceGradientRoute();
    } else {
      // Faz a linha normal
      const polyline = new this.googleMaps.Polyline({
        path: this.gmapPoints,
        geodesic: true,
        strokeColor: this.polylineStrokeColor,
        strokeWeight: this.polylineStrokeWeight,
        map: this.map
      });

      this.customPolylines = [polyline];
    }

    if (!supressMarkers) {
      this.makeInitialMarker(this.gmapPoints[0]);
      this.makeFinalMarker(this.gmapPoints[this.gmapPoints.length - 1]);
    }

    if (!supressMarkers && (window.environment !== 'production' || subDomain === subDomainInput)) {
      this.createAdditionalPoints();
    }
  }

  /**
   * Faz um degradê do azul para o verde para diferenciar
   * na rota qual linha é ida e qual é volta
   *
   * @return void
   */
  traceGradientRoute() {
    // De quantos em quantos pontos a cor vai variar em +1
    const variacaoCor = Math.ceil(this.gmapPoints.length / 255);
    let red = '00';
    let green = 'ff';
    const polylines = [];

    for(let i = 0; i < 255; i++) {
      // Sempre pegar um anterior para fazer a continuação da reta
      let start = i * variacaoCor - 1;
      // Se for o primeiro nao pode começar com index -1
      start = start < 0 ? 0 : start;
      const end = (i + 1) * variacaoCor;

      const pontosCor = this.gmapPoints.slice(start, end);

      red = addIntToHex(red, 1);
      green = addIntToHex(green, -1);

      // Completa com 0 a esquerda se o hexadecimal tiver 1 casa só
      red = red.length < 2 ? '0' + red : red;
      green = green.length < 2 ? '0' + green : green;

      // Linha com o caminho
      const polyline = new this.googleMaps.Polyline({
        path: pontosCor,
        geodesic: true,
        strokeColor: '#' + red + green + '00',
        strokeWeight: this.polylineStrokeWeight,
        map: this.map
      });

      polylines.push(polyline);
    }

    return polylines;
  }

  /**
   * Marca os pontos adicionais na rota (pontos azul no meio do percurso)
   *
   * @return void
   */
  createAdditionalPoints() {
    this.gmapPoints.forEach((point, index) => {
      // Menos o primeiro e o ultimo
      if (index === 0 || index === this.gmapPoints.length - 1) {
        return;
      }

      let title = '';
      for (const key in point) {
        title += key + ': "' + point[key] + '"; ';
      }

      this.makeWaypointMarker(point, title);
    });

    // Adiciona evento no botão do admin para sumir ou mostrar
    // todos os pontos do percurso do gps
    if (this.mapElement.id) {
      const btnToggleMarkers = document.querySelectorAll(
        '[data-toggle-markers=' + this.mapElement.id + ']'
      );

      if (btnToggleMarkers.length) {
        btnToggleMarkers.forEach((btn) => {
          btn.addEventListener('click', () => {
            if (!this.markers.length) {
              return;
            }

            const valueMap = this.markers[0].getMap() == null ? this.map : null;

            this.markers.forEach((marker) => {
              marker.setMap(valueMap);
            });
          });
        });
      }
    }
  }

  /**
   * Faz o marcador inicial no mapa na posicao passada
   *
   * @param  array position
   * @param  string title
   *
   * @return void
   */
  makeInitialMarker(position, title) {
    // Marcador inicial
    const icon = 'https://maps.google.com/mapfiles/ms/icons/green-dot.png';
    this._makeMarker(position, title, icon);
  }

  /**
   * Faz o marcador final no mapa na posicao passada
   *
   * @param  array position
   * @param  string title
   *
   * @return void
   */
  makeFinalMarker(position, title) {
    // Marcador final
    const icon = 'https://maps.google.com/mapfiles/ms/icons/red-dot.png';
    this._makeMarker(position, title, icon);
  }

  /**
   * Faz o marcador de uma parada no mapa na posicao passada
   *
   * @param  array position
   * @param  string title
   *
   * @return void
   */
  makeWaypointMarker(position, title) {
    // Marcador de parada
    const icon = 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png';
    this._makeMarker(position, title, icon);
  }

  /**
   * Função base que faz os marcadores no mapa
   *
   * @param  array  position
   * @param  string title
   * @param  string icon
   *
   * @return void
   */
  _makeMarker(position, title, icon) {
    const marker = new this.googleMaps.Marker({
      position: position,
      title: title,
      icon: icon,
      map: this.map,
    });

    this.markers.push(marker);
  }

  /**
   * Remove os marcadores do mapa
   *
   * @return void
   */
  clearMarkers() {
    this.markers.forEach(function (marker) {
      marker.setMap(null);
    });

    this.markers = [];
  }

  /**
   * Remove as polylines do mapa
   *
   * @return void
   */
  clearPolylines() {
    this.customPolylines.forEach(function (polyline) {
      polyline.setMap(null);
    });

    this.customPolylines = [];
  }

  /**
   * Metodo que inicializa a classe por outros componentes que
   * o importarem
   *
   * @return void
   */
  static init() {
    const divMaps = document.querySelectorAll('div[data-gmap]');

    if (divMaps.length) {
      const options = {
        key: window.googleApiKey,
        libraries: ['places', 'geometry'],
        language: Lang.getLocale(),
        v: 3
      };

      loadGMaps(options).then((googleMaps) => {
        divMaps.forEach(function (divMap) {
          try {
            new Gmaps(divMap, googleMaps);
          } catch (e) {
            console.error(e);
          }
        });
      }).catch((err) => {
        console.error(err);
        GlobalAlerts.createAndShow({
          title: Lang.get('js/src/classes/gmaps.ops'),
          message: Lang.get('js/src/classes/gmaps.something_went_wrong'),
          type: 'danger'
        });
      });
    }
  }
}

export default Gmaps;
